import React, { FC, useState, useMemo } from "react";
import InfoCard from "./../../InfoCard";
import SunLine from "./../objects/SunLine";
import MoonLine from "./../objects/MoonLine";
import MercuryLine from "./../objects/MercuryLine";
import VenusLine from "./../objects/VenusLine";
import MarsLine from "./../objects/MarsLine";
import JupiterLine from "./../objects/JupiterLine";
import SaturnLine from "./../objects/SaturnLine";
import UranusLine from "./../objects/UranusLine";
import NeptuneLine from "./../objects/NeptuneLine";
import PlutoLine from "./../objects/PlutoLine";
import EarthCute from "./../objects/EarthCute";

export interface CelestialObjectSVGProps {
  x: number;
  y: number;
  width: number;
  height: number;
  fg: string;
  bg: string;
}

export type CelestialObjectComponent = {
  label: string;
  name: string;
  component: FC<CelestialObjectSVGProps>;
  image: string;
  description: string;
};

export const CELESTIAL_OBJECTS_SETTING: CelestialObjectComponent[] = [
  {
    label: "Pluto",
    name: "pluto",
    component: PlutoLine,
    image: "/data/celestials/pluto.jpg",
    description: `## The Dwarf Planet

**Pluto** was once considered the ninth planet but was reclassified as a dwarf planet in 2006. It has a mass about **0.0022 times** that of Earth and orbits at an average distance of **5.9 billion km** (about **39.5 AU**). Pluto has a highly elliptical orbit and five known moons, with Charon being the largest, almost half the size of Pluto itself.`,
  },
  {
    label: "Neptune",
    name: "neptune",
    component: NeptuneLine,
    image: "/data/celestials/neptune.jpg",
    description: `## The Windy Planet

**Neptune** is the eighth planet from the Sun, with a mass about **17 times** that of Earth. It orbits at an average distance of **4.5 billion km** (about **30.1 AU**). Neptune is known for its strong winds, which can reach speeds of over **2,000 km/h** (1,200 mph). It has at least **14 moons**, with Triton being the largest.`,
  },
  {
    label: "Uranus",
    name: "uranus",
    component: UranusLine,
    image: "/data/celestials/uranus.jpg",
    description: `## The Ice Giant

**Uranus** is the seventh planet from the Sun and has a mass about **14.5 times** that of Earth. It orbits at an average distance of **2.87 billion km** (about **19.2 AU**). Uranus is unique for its axial tilt of about **98 degrees**, causing it to rotate on its side. Its atmosphere is rich in methane, giving it a blue-green color, and it has at least **27 moons**.`,
  },
  {
    label: "Saturn",
    name: "saturn",
    component: SaturnLine,
    image: "/data/celestials/saturn.jpg",
    description: `## The Ringed Planet

**Saturn** is the sixth planet from the Sun, distinguished by its prominent ring system made up of ice and rock particles. It has a mass about **95 times** that of Earth and orbits at an average distance of **1.43 billion km** (about **9.58 AU**). Saturn has over **82 moons**, with Titan being the largest, surpassing even Mercury in size.`,
  },
  {
    label: "Mercury",
    name: "mercury",
    component: MercuryLine,
    image: "/data/celestials/mercury.jpg",
    description: `## The Swift Planet

**Mercury** is the closest planet to the Sun, orbiting at an average distance of **57.9 million km** (about **0.39 AU**). It has a mass approximately **0.055 times** that of Earth. Mercury has no atmosphere and experiences extreme temperature variations, from **430°C** (800°F) during the day to **-180°C** (-290°F) at night. It completes an orbit around the Sun in just **88 Earth days**.`,
  },
  {
    label: "Mars",
    name: "mars",
    component: MarsLine,
    image: "/data/celestials/mars.jpg",
    description: `## The Red Planet

**Mars** is the fourth planet from the Sun and is known for its reddish appearance due to iron oxide on its surface. It has a mass about **0.11 times** that of Earth and orbits at an average distance of **227.9 million km** (about **1.52 AU**). Mars has the largest volcano and canyon in the solar system and is a prime candidate for future human exploration due to its relatively hospitable conditions.`,
  },
  {
    label: "Jupiter",
    name: "jupiter",
    component: JupiterLine,
    image: "/data/celestials/jupiter.jpg",
    description: `## The Gas Giant

**Jupiter** is the fifth planet from the Sun and the largest in the solar system, with a mass about **318 times** that of Earth. It orbits at an average distance of **778.5 million km** (about **5.20 AU**). Jupiter is known for its Great Red Spot, a massive storm larger than Earth, and has at least **79 moons**, including the four large Galilean moons: Io, Europa, Ganymede, and Callisto.`,
  },
  {
    label: "Venus",
    name: "venus",
    component: VenusLine,
    image: "/data/celestials/venus.jpg",
    description: `## Earth's Twin

**Venus** is the second planet from the Sun and is often called Earth's twin due to its similar size and composition. It has a mass about **0.82 times** that of Earth and orbits at an average distance of **108.2 million km** (about **0.72 AU**). Venus has a thick, toxic atmosphere rich in carbon dioxide, with surface temperatures averaging **465°C** (869°F), making it the hottest planet in the solar system.`,
  },
  {
    label: "Moon",
    name: "moon",
    component: MoonLine,
    image: "/data/celestials/moon.jpg",
    description: `## Earth's Natural Satellite

The **Moon** is Earth's only natural satellite and the fifth-largest moon in the solar system. It has about **1/81** the mass of Earth and orbits at an average distance of **384,400 km** (about **1.28 light-seconds**). The Moon's gravitational pull causes tides on Earth and has a stabilizing effect on the planet's axial tilt, contributing to a stable climate.`,
  },
  {
    label: "Sun",
    name: "sun",
    component: SunLine,
    image: "/data/celestials/sun.jpg",
    description: `## The Heart of Our Solar System

The **Sun** is a G-type main-sequence star that constitutes about **99.86%** of the total mass of the solar system. Its immense gravitational pull keeps the planets, asteroids, and comets in orbit. The Sun is approximately **109 times** the diameter of Earth, and its energy supports almost all life on Earth via photosynthesis and drives Earth's climate and weather.`,
  },
];

export type Celestial = {
  ecliptic: {
    lat: number;
    lon: number;
  };
};

export type Celestials = {
  [key: string]: Celestial;
};

interface Props {
  r: number;
  celestials: Celestials;
  west: number;
  fg: string;
  bg: string;
  canvas: number[];
  dateTime: Date;
  locationName: string;
  /** Opens the date/time/location picker on the tapped line's tab. */
  onInfoClick: (tab: number) => void;
}

const CelestialCircle: FC<Props> = ({
  r,
  celestials,
  west,
  fg,
  bg,
  canvas,
  dateTime,
  locationName,
  onInfoClick,
}) => {
  const [selectedObject, setSelectedObject] =
    useState<null | CelestialObjectComponent>(null);

  const handleOpen = (object: CelestialObjectComponent) => {
    setSelectedObject(object);
  };

  const handleClose = () => {
    setSelectedObject(null);
  };

  // Memoize values that only depend on canvas (static) or dateTime/locationName
  // (which change only every 5 minutes, not on animation frames)
  const objectDiameter = useMemo(
    () => (600 / Math.min(canvas[0], canvas[1])) * 70,
    [canvas],
  );
  const earthDiameter = useMemo(
    () => (600 / Math.min(canvas[0], canvas[1])) * 100,
    [canvas],
  );
  const fontSize = useMemo(
    () => Math.round((Math.min(canvas[0], canvas[1]) / 600) * 9),
    [canvas],
  );
  const lineHeight = useMemo(() => fontSize * 1.6, [fontSize]);

  const dateStr = useMemo(() => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const day = String(dateTime.getDate()).padStart(2, "0");
    const mon = months[dateTime.getMonth()];
    const year = dateTime.getFullYear();
    return `${day} ${mon} ${year}`;
  }, [dateTime]);

  const timeStr = useMemo(() => {
    const hh = String(dateTime.getHours()).padStart(2, "0");
    const mm = String(dateTime.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  }, [dateTime]);

  return (
    <g>
      <circle
        key={`celestials-circle`}
        fill="none"
        strokeWidth="0.5"
        stroke={fg}
        cx={canvas[0] / 2}
        cy={canvas[1] / 2}
        r={r}
      />
      <g transform={`translate(${canvas[0] / 2}, ${canvas[1] / 2})`}>
        <g transform={`rotate(${west - 180})`}>
          <EarthCute
            x={-earthDiameter / 2}
            y={-earthDiameter / 2}
            width={earthDiameter}
            height={earthDiameter}
            fg={fg}
            bg={bg}
          />
          <g
            textAnchor="middle"
            fontFamily="Overlock, Roboto, Helvetica, Arial, sans-serif"
            fontSize={fontSize}
            fill={fg}
            opacity={0.8}
            cursor="pointer"
          >
            {/* Date and time on one line, location underneath. Tapping a part
                of the line opens the picker on the matching tab:
                0 = Date, 1 = Time, 2 = Location. */}
            <text x={0} y={earthDiameter / 2 + fontSize * 2}>
              <tspan onClick={() => onInfoClick(0)}>{dateStr}</tspan>
              <tspan onClick={() => onInfoClick(1)}>{" - " + timeStr}</tspan>
            </text>
            <text
              x={0}
              y={earthDiameter / 2 + fontSize * 2 + lineHeight}
              onClick={() => onInfoClick(2)}
            >
              {locationName}
            </text>
          </g>
        </g>
      </g>
      {CELESTIAL_OBJECTS_SETTING.map((object, index) => {
        const body = celestials[object.name];

        const width = objectDiameter;
        const height = objectDiameter;

        const cx = canvas[0] / 2;
        const cy = canvas[1] / 2;

        const Component = object.component;
        return (
          <g
            key={`${index}-group`}
            onClick={() => handleOpen(object)}
            cursor={"pointer"}
            transform={`rotate(${body.ecliptic.lon} ${cx} ${cy})`}
          >
            <g
              transform={`translate(${cx + r}, ${cy}) rotate(${west - 180 - body.ecliptic.lon})`}
            >
              <Component
                key={index}
                x={-width / 2}
                y={-height / 2}
                width={width}
                height={height}
                fg={bg}
                bg={fg}
              />
            </g>
          </g>
        );
      })}
      {selectedObject && (
        <InfoCard
          open={!!selectedObject}
          onClose={handleClose}
          image={selectedObject.image}
          title={selectedObject.label}
          description={selectedObject.description}
        />
      )}
    </g>
  );
};

export default React.memo(CelestialCircle);
